import { AppBar, Grid, Toolbar, useTheme } from "@mui/material";
import {
	BackdropCircularProgress,
	EmptyList,
	EnhancedSnackbar,
} from "@websential/cosmic-v20";
import {
	EMPLOYEE_LIST_SCREEN_SLUG,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../../../common/constants";
import { FormProvider, useForm } from "react-hook-form";
import { GET_QUERY_KEY, QUERY_OPTIONS } from "../../../../common/constants";
import { IResponse, IServerError } from "../../../../models/common";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	formatDate,
	useNavigateToLogout,
	useRemoveQueriesOnUnmount,
		sortDDLByLabel,
} from "../../../../common/Common";
import {
	setSelectedActivityLogActions,
	setSelectedStickyNavBar,
} from "../../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AuthService } from "../../../../services";
import { EmployeeService } from "../../../../services/EmployeeService";
import FileSaver from "file-saver";
import { IAppState } from "../../../../store/reducers";
import { IDropDownListResponse } from "../../../../models/common/IDropDownListResponse";
import { IGetListRequest } from "../../../../models";
import { LookupFieldService } from "../../../../services/LookupFieldService";
import { PaydayAdvanceReportDataGridWithColumnToggled } from "./PaydayAdvanceReportDataGridWithColumnToggled";
import { PaydayAdvanceReportHeader } from "./PaydayAdvanceReportHeader";
import { ReportsService } from "../../../../services/ReportsService";
import { setSelectedDrawerOptionsMenu } from "../../../../store/actions/drawerMenuOptionsActions";
import { useNavigate } from "react-router-dom";

export interface IPaydayAdvanceReport { }
const PAYDAY_ADVANCE_REPORT_QUERY_KEY = "payday-advance-report";

const usePaydayAdvanceReport = (
	runGetListAPICall: boolean,
	clientId: string,
	taxYear: number,
	employeeId: string,
	startDate: string | null,
	endDate: string | null,
	params: IGetListRequest, // must be serializable/stable,
	navigateToLogout: (path: any) => void
) =>
	useQuery({
		queryKey: [
			PAYDAY_ADVANCE_REPORT_QUERY_KEY,
			runGetListAPICall,
			taxYear,
			clientId,
			employeeId,
			startDate,
			endDate,
			params,// make sure params is stable (memoized or primitive values)
		],
		queryFn: () =>
			ReportsService.getPaydayAdvanceReport(
				runGetListAPICall,
				clientId,
				taxYear,
				employeeId,
				startDate,
				endDate,
				params,
				navigateToLogout,
			),
		refetchOnWindowFocus: false,
	});


const useGetEmployeeDDL = (clientId: string) =>
	useQuery({
		queryKey: ["employee-status-ddl", clientId],
		queryFn: () => {
			return EmployeeService.getEmployeeDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});


export const PaydayAdvanceReport: React.FC<IPaydayAdvanceReport> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const clientIdStore = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const clientName = useSelector((state: IAppState) => state.client.clientName);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();

	// for delete row Purpose
	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [isLoading, setIsLoading] = useState(false);
	const [runGetListAPICall, setRunGetListAPICall] =
		useState<any>(true);
	const [paydayAdvanceReportData, setPaydayAdvanceReportData] =
		useState<any>(undefined);


	const { isLoading: isLoadingEmployeeDDL, data: employeeDDL } = useGetEmployeeDDL(clientId);

	const formMethods = useForm<any>({
		// resolver: yupResolver(ValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
	});


	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "firstName",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const employeeId = formMethods.getValues("employee");
	const startDate = formMethods.getValues("startDate");
	const endDate = formMethods.getValues("endDate");

	const { isPending, data } = usePaydayAdvanceReport(
		runGetListAPICall,
		clientIdStore,
		taxYear,
		employeeId && employeeId,
		formatDate(startDate),
		formatDate(endDate),
		queryParams,
		navigateToLogout
	);
	const paydayAdvanceList: any | null = data;

	useEffect(() => {
		if (paydayAdvanceList) {
			setPaydayAdvanceReportData(paydayAdvanceList);
		}
	}, [paydayAdvanceList]);

	//   after click apply button api calls with updated values and update payday report data
	const paydayAdvanceReportDataAPICall = (
		employeeId: string,
		startDate: string,
		endDate: string,
	) => {
		const fetchData = async () => {
			setIsLoading(true);

			try {
				const response: any = await ReportsService.getPaydayAdvanceReport(
					true,
					clientIdStore,
					taxYear,
					employeeId,
					formatDate(startDate),
					formatDate(endDate),
					queryParams,
					navigateToLogout
				);

				if (response) {
					setPaydayAdvanceReportData(response);
				}
			} catch {
			} finally {
				setIsLoading(false);
			}
		};
		fetchData();
	};

	const onSubmit = (values: any) => {


		paydayAdvanceReportDataAPICall(
			values.employee,
			values.startDate,
			values.endDate,
		);
	};

		const employeeDDLItems = useMemo(() => {
		if (employeeDDL) {
			if (
				employeeDDL &&
				Array.isArray(employeeDDL)
			) {
				let resources: IDropDownListResponse[] =
						sortDDLByLabel(employeeDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [employeeDDL]);

	
	const [zeroEmployee, setzeroEmployee] = useState(true);

	const [submitAddRemoveColumnsHandler, setSubmitAddRemoveColumnsHandler] =
		useState(undefined);
	const [onColumnReorder, setOnColumnReorder] = useState(undefined);

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "firstName",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const {
		isPending: isSubmittingAddRemoveHeaderColumns,
		mutate: submitAddRemoveHeaderColumns,
	} = useMutation({
		mutationFn: ReportsService.submitPaydayAdvanceReportAddRemoveHeaderColumns,
		onSuccess: (res: any | IServerError | IResponse) => {
			if (res.status === 200) {
				return queryClient.invalidateQueries({
					queryKey: [PAYDAY_ADVANCE_REPORT_QUERY_KEY],
				});
			} else {
				setSnackbarData({
					open: true,
					message: res.message
						? res.message
						: "Something went wrong, try again later!",
					severity: "error",
				});
			}
		},
		onError: (err: any) => {
			setSnackbarData({
				open: true,
				message: err.message,
				severity: "error",
			});
		},
	});

	useEffect(() => {


		if (submitAddRemoveColumnsHandler) {
			submitAddRemoveHeaderColumns({
				clientId: clientId,
				columns: submitAddRemoveColumnsHandler,
				navigateToLogout: navigateToLogout,
			});
		}
	}, [submitAddRemoveColumnsHandler]);


	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	useRemoveQueriesOnUnmount(["employee-report"]);

	const updatedPaydayAdvanceListItems = useMemo(() => {
		return (
			paydayAdvanceReportData &&
			paydayAdvanceReportData.items &&
			paydayAdvanceReportData?.items.map((item: any) => ({
				...item,

			}))
		);
	}, [paydayAdvanceReportData]);

	const expandedLabels = ["Reports", "Employees"];
	const expandedPaydayAdvanceListOrSetupItem = "Payday Advance"; // Special case for "List" under "Employee"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Employees" &&
				item.label === expandedPaydayAdvanceListOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedPaydayAdvanceListOrSetupItem && "/reports/payday-advance",
			};
			return enhancedItem;
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: EMPLOYEE_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));

		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
	}, [drawerMenuOptions]);

	// getting latest updated values of form fields using watch method
		const { watch } = formMethods;
		const values = watch([
			"employee",
			"startDate",
			"endDate",
		]);
	
		const savePdfAsFile = (xmlData: any, filename: any) => {
			const blob = new Blob([xmlData], { type: "pdf;charset=utf-8" });
			FileSaver.saveAs(blob, filename);
		};
	
		const onExportButtonClick = async (selectedOption: string) => {
			const [employee, startDate, endDate] = values;
	
			try {
				setIsLoading(true);
				const response = await ReportsService.exportPaydayAdvanceReport(
					selectedOption,
					clientIdStore,
					taxYear,
					employee,
					formatDate(startDate),
					formatDate(endDate),
					queryParams
				);
	
				if (response) {
					if (selectedOption === "PDF") {
						if (response.status === 400) {
							setSnackbarData({
								open: true,
								message: response.data.message
									? response.data.message
									: "Something went wrong.",
	
								severity: "error",
							});
						} else {
							const filename = `${clientName}-employee.pdf`;
							savePdfAsFile(response, filename);
						}
					} else if (selectedOption === "XLS") {
						if (response.status === 400) {
							setSnackbarData({
								open: true,
								message: response.data.message
									? response.data.message
									: "Something went wrong.",
	
								severity: "error",
							});
						} else {
							const filename = `${clientName}-employee.xlsx`;
							savePdfAsFile(response, filename);
						}
					} else {
						const fileType =
							selectedOption === "XLS"
								? "application/vnd.ms-excel"
								: "text/csv;charset=utf-8;";
						const fileExtension = selectedOption === "XLS" ? "xls" : "csv";
	
						const blob = new Blob([response], { type: fileType });
						FileSaver.saveAs(blob, `${clientName}-employee.${fileExtension}`);
					}
				} else {
					console.error("Error: No data received from the server");
				}
			} catch (err) {
	
				setIsLoading(false);
			} finally {
				setIsLoading(false);
			}
	
		};
	


	const image =
		"https://taxslips.blob.core.windows.net/taxslips-public-images-container/empty_employee_list.png";

	return (
		<>
			<div
				style={{
					margin: "15px 0",
					fontSize: "1.5rem",
					fontWeight: "bold",
					fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
				}}
			>
				{"Payday Advance Report"}
			</div>

			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<PaydayAdvanceReportHeader
						employeeDDLItems={employeeDDLItems}
						setStateRunGetListAPICall={(value: boolean) => setRunGetListAPICall(value)}
					/>
				</form>
			</FormProvider>

			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<BackdropCircularProgress
					open={
						isPending ||
						isSubmittingAddRemoveHeaderColumns ||
						isLoadingEmployeeDDL ||
						isLoading
					}
					testId="employee-report"
				/>

				<PaydayAdvanceReportDataGridWithColumnToggled
					columns={
						paydayAdvanceReportData && paydayAdvanceReportData.headerColumns
							? paydayAdvanceReportData.headerColumns.filter(
								(item: any) => item.field !== "id"
							)
							: []
					}
					completeHeaderColumnsList={
						paydayAdvanceReportData && paydayAdvanceReportData.totalHeaderColumns &&
							Object.keys(paydayAdvanceReportData.totalHeaderColumns).length !== 0
							? paydayAdvanceReportData.totalHeaderColumns
							: {}
						// paydayAdvanceReportData?.headerColumns ?? []
					}
					submitAddRemoveColumnsHandler={(value: any) =>
						setSubmitAddRemoveColumnsHandler(value)
					}
					onColumnReorder={(value: any) => setOnColumnReorder(value)}
					rows={
						paydayAdvanceReportData && updatedPaydayAdvanceListItems
							? updatedPaydayAdvanceListItems
							: []
						// paydayAdvanceReportData?.items ?? []
						// itams
					}
					showIndexColumn={true}
					showChecboxColumn={false}
					showSettingColumn={false}

					onSnackbarMessageData={(value: any) => setSnackbarData(value)}
					totalRecords={
						paydayAdvanceReportData && paydayAdvanceReportData.total
							? paydayAdvanceReportData.total
							: 0
					}
					initialPageSize={INITIAL_PAGE_SIZE}
					rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
					onRequestParamsChange={onRequestParamsChange}
					searchFieldPlaceholder={"Search by Name"}
					changeLoginForGetEmptyItemsBySearchQuery={(value: any) =>
						setzeroEmployee(value)
					}
					pageTitle={"Employee Report"}
					onExportButtonClick={onExportButtonClick}

				/>

				{paydayAdvanceReportData ? (
					<AppBar
						position="sticky"
						color="default"
						elevation={1}
						sx={{
							backgroundColor: "#F7F7F7",

							boxShadow: "none",
							alignItems: "center",
							zIndex: 0,
							position: "sticky",
							bottom: 0,
						}}
					></AppBar>
				) : (
					<></>
				)}
			</Grid>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
